import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { removeOldestQuery } from "@tanstack/react-query-persist-client";

// How long a persisted cache stays valid across reloads before it's discarded
// entirely (independent of any individual query's staleTime).
export const PERSIST_MAX_AGE = 30 * 60 * 1000; // 30 minutes

// A stable identifier that changes per deploy, so an incompatible persisted
// cache shape (e.g. after a query-key refactor) is dropped instead of hydrating
// stale/broken data. CRA exposes any REACT_APP_* env var to the client bundle.
//
// DEPLOY HOOK: set REACT_APP_BUILD_ID at build time (e.g. `REACT_APP_BUILD_ID=$(git
// rev-parse --short HEAD) npm run build`, or wire it into CI as the commit SHA/build
// number) so every deploy gets its own buster and persisted caches are invalidated
// automatically on release. Until that's wired in CI, this falls back to the
// package.json "version" (bump it on breaking cache-shape changes), then a constant.
export const PERSIST_BUSTER =
	process.env.REACT_APP_BUILD_ID ||
	process.env.REACT_APP_VERSION ||
	"0.1.0"; // package.json "version" at build time — bump on cache-shape changes

// A minimal in-memory Storage shim so makePersister() is safe to call without a
// real localStorage (SSR, locked-down browsers, or plain unit tests). It only
// needs to satisfy createSyncStoragePersister's Storage interface.
function createNoopStorage() {
	const store = new Map();
	return {
		getItem: (key) => (store.has(key) ? store.get(key) : null),
		setItem: (key, value) => {
			store.set(key, value);
		},
		removeItem: (key) => {
			store.delete(key);
		},
	};
}

function resolveDefaultStorage() {
	if (typeof window !== "undefined" && window.localStorage) {
		return window.localStorage;
	}
	return createNoopStorage();
}

/**
 * Builds a react-query sync-storage persister. `storage` is injectable (any
 * object with getItem/setItem/removeItem) so this is unit-testable without a
 * real browser; it defaults to window.localStorage when available, and falls
 * back to a no-op in-memory shim otherwise (SSR/test-safe).
 */
export function makePersister(storage) {
	return createSyncStoragePersister({
		storage: storage || resolveDefaultStorage(),
		// The board-figure payloads (e.g. 182-day forecast band/horizon series)
		// plus the whole app cache can exceed localStorage's ~5 MB quota. Without a
		// retry, createSyncStoragePersister lets the setItem quota error throw out of
		// the throttled timer callback (uncaught). removeOldestQuery drops the oldest
		// persisted query and retries the write, so persistence degrades gracefully
		// under quota pressure instead of erroring.
		retry: removeOldestQuery,
	});
}
